import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaArrowRight,
  FaWhatsapp,
  FaCheckCircle,
  FaPlane,
  FaGraduationCap,
  FaTrophy,
  FaBars,
  FaTimes,
  FaEnvelope
} from 'react-icons/fa';
import Header from './components/Header';
import Footer from './components/Footer';
import { WHATSAPP_NUMBER } from './config/navItems';
import { useLanguage } from './i18n';

const RoadMapPage = () => {
  const { t, tArray } = useLanguage();
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    interestLevel: ''
  });
  const levelRefs = useRef([]);

  useEffect(() => {
    // Para SEO: actualizar el título de la página
    document.title = "Wind Tunnel Training Roadmap | Flight Progression Levels";

    const handleScroll = () => {
      // Auto-cerrar el sidebar al hacer scroll en móviles
      if (window.innerWidth < 768 && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sidebarOpen]);

  const scrollToLevel = (index) => {
    if (levelRefs.current[index]) {
      levelRefs.current[index].scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setSelectedLevel(index);
      // En móviles, cerrar el sidebar después de seleccionar
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    }
  };

  const handleWhatsAppRedirect = (message, includeFormData = false) => {
    let fullMessage = message;
    
    // Si se solicita incluir datos del formulario y hay datos disponibles
    if (includeFormData && formData.name) {
      fullMessage += `\n\nContact Information:`;
      if (formData.name) fullMessage += `\nName: ${formData.name}`;
      if (formData.email) fullMessage += `\nEmail: ${formData.email}`;
      if (formData.interestLevel) fullMessage += `\nInterested in Level: ${formData.interestLevel}`;
      if (formData.message) fullMessage += `\nMessage: ${formData.message}`;
    }
    
    const encodedMessage = encodeURIComponent(fullMessage);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
  };

  const handleLevelInterest = (level) => {
    window.location.href = `/contact?level=${encodeURIComponent(level.level)}&message=${encodeURIComponent(`I'm interested in training for ${level.level}: ${level.description}`)}`;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    handleWhatsAppRedirect(`Interest in Wind Tunnel Training`, true);
    setFormData({ name: '', email: '', message: '', interestLevel: '' });
  };

  // Styling information for each level (colors are not translatable)
  const levelStyles = [
    { color: "from-gray-400 to-gray-500", bgClass: "bg-gray-300" },
    { color: "from-red-300 to-red-400", bgClass: "bg-red-300" },
    { color: "from-red-400 to-red-500", bgClass: "bg-red-400" },
    { color: "from-red-500 to-red-600", bgClass: "bg-red-500" },
    { color: "from-red-600 to-red-700", bgClass: "bg-red-600" },
    { color: "from-blue-300 to-blue-400", bgClass: "bg-blue-300" },
    { color: "from-blue-400 to-blue-500", bgClass: "bg-blue-400" },
    { color: "from-blue-500 to-blue-600", bgClass: "bg-blue-500" },
    { color: "from-blue-600 to-blue-700", bgClass: "bg-blue-600" },
    { color: "from-black to-gray-900", bgClass: "bg-black text-white" },
    { color: "from-gray-900 to-gray-800", bgClass: "bg-gray-800 text-white" },
    { color: "from-gray-800 to-gray-700", bgClass: "bg-gray-700 text-white" },
    { color: "from-yellow-200 to-yellow-300", bgClass: "bg-yellow-200" },
    { color: "from-yellow-300 to-yellow-400", bgClass: "bg-yellow-300" },
    { color: "from-green-300 to-green-400", bgClass: "bg-green-300" },
    { color: "from-green-400 to-green-500", bgClass: "bg-green-400" },
    { color: "from-cyan-400 to-cyan-500", bgClass: "bg-cyan-400" },
    { color: "from-pink-500 to-purple-500", bgClass: "bg-pink-500 text-white" }
  ];

  // Get translated levels and merge with styling
  const translatedLevels = tArray('roadmap.levels');
  const roadmapLevels = translatedLevels.map((levelData, index) => ({
    ...levelData,
    ...levelStyles[index]
  }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-900 to-gray-900">
      {/* Header Component */}
      <Header />

      {/* Hero Section con paddingTop dinámico para móviles */}
      <div className="relative pt-36 pb-12 md:pt-40 md:pb-16 px-4">  
        <div className="max-w-6xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-4 md:mb-6"
          >
            {t('roadmap.title')} <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">{t('roadmap.titleHighlight')}</span>
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto mb-6 md:mb-8"
          >
            {t('roadmap.subtitle')}
          </motion.p>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex justify-center gap-4"
          >
            <button
              onClick={() => handleWhatsAppRedirect("I want to start my wind tunnel training journey!")}
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 md:px-8 py-3 rounded-full hover:shadow-lg hover:shadow-orange-500/50 transition-all transform hover:scale-105 flex items-center text-sm md:text-base"
            >
              {t('roadmap.startJourney')} <FaArrowRight className="ml-2" />
            </button>
          </motion.div>
        </div>
      </div>

      {/* Mobile Toggle Button for Sidebar - Mejor posicionado */}
      <div className="fixed bottom-20 right-6 md:hidden z-40">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 rounded-full shadow-xl hover:shadow-orange-500/50 transition-all"
          aria-label={sidebarOpen ? "Close roadmap navigation" : "Open roadmap navigation"}
        >
          {sidebarOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
        </button>
      </div>

      {/* Interactive Roadmap */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-16">
        <div className="grid md:grid-cols-12 gap-8">
          {/* Sidebar - Optimizado para móviles */}
          <AnimatePresence>
            {(sidebarOpen || window.innerWidth >= 768) && (
              <motion.div 
                className={`md:col-span-3 fixed md:relative z-30 ${sidebarOpen ? 'inset-0 md:inset-auto' : 'hidden md:block'}`}
                initial={{ x: -300, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -300, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="md:sticky top-24 bg-gray-900/95 md:bg-gray-800/50 h-full md:h-auto overflow-y-auto md:overflow-visible p-6 backdrop-blur-sm border border-orange-500/20 rounded-none md:rounded-xl">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white">{t('roadmap.sidebarTitle')}</h3>
                    <button 
                      className="md:hidden text-white"
                      onClick={() => setSidebarOpen(false)}
                      aria-label="Close navigation"
                    >
                      <FaTimes size={20} />
                    </button>
                  </div>
                  <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2 pb-16 md:pb-0">
                    {roadmapLevels.map((item, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ x: 10 }}
                        onClick={() => scrollToLevel(index)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                          selectedLevel === index
                            ? 'bg-gradient-to-r ' + item.color + ' text-white shadow-lg'
                            : 'text-gray-300 hover:bg-gray-700/50'
                        }`}
                      >
                        <div className="font-bold">{item.level}</div>
                        <div className="text-sm opacity-80">{item.description}</div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main Content */}
          <div className="md:col-span-9 w-full">
            <div className="space-y-6 md:space-y-8">
              {roadmapLevels.map((level, index) => (
                <motion.div
                  key={index}
                  ref={el => levelRefs.current[index] = el}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-gray-800/50 rounded-xl p-5 md:p-8 backdrop-blur-sm border-2 ${
                    selectedLevel === index ? 'border-orange-500' : 'border-gray-700'
                  }`}
                  id={`level-${level.level.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '-')}`}
                >
                  <div className="flex items-center mb-4 md:mb-6">
                    <div className={`p-3 md:p-4 rounded-xl bg-gradient-to-r ${level.color}`}>
                      {index < 5 ? <FaPlane className="text-xl md:text-3xl text-white" /> :
                       index >= 16 ? <FaTrophy className="text-xl md:text-3xl text-white" /> :
                       <FaGraduationCap className="text-xl md:text-3xl text-white" />}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg md:text-2xl font-bold text-white">{level.level}</h3>
                      <p className="text-orange-500 text-sm md:text-base">{level.description}</p>
                    </div>
                  </div>

                  {level.skills && (
                    <div className="mt-4">
                      <h4 className="text-base md:text-lg font-semibold text-white mb-3 md:mb-4">{t('roadmap.skillsTitle')}</h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {level.skills.map((skill, idx) => (
                          <li key={idx} className="flex items-center text-gray-300 text-sm md:text-base">
                            <FaCheckCircle className="text-green-500 mr-2 flex-shrink-0 text-sm md:text-base" />
                            <span>{skill}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-5 md:mt-6">
                    <button
                      onClick={() => handleLevelInterest(level)}
                      className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg hover:shadow-lg hover:shadow-orange-500/50 transition-all transform hover:scale-105 flex items-center justify-center text-sm md:text-base"
                    >
                      {t('roadmap.trainButton')} <FaWhatsapp className="ml-2" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <section id="contact" className="py-12 md:py-16 bg-gradient-to-b from-gray-100 to-white">
        <div className="py-12 md:py-16 bg-gradient-to-r from-orange-500 to-red-500">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 md:mb-6">
                {t('roadmap.readyToBegin.title')}
              </h2>
              <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8 max-w-2xl mx-auto">
                {t('roadmap.readyToBegin.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/contact"
                    className="bg-transparent border-2 border-white text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-sm md:text-lg font-bold hover:bg-white/10 transition-all flex items-center justify-center"
                  >
                    <FaEnvelope className="mr-2" />
                    {t('roadmap.readyToBegin.contactButton')}
                  </a>

                  <button
                    onClick={() => handleWhatsAppRedirect("Hi! I'm interested in wind tunnel coaching. Can we talk?")}
                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-full transition-all transform hover:scale-105 flex items-center justify-center text-lg font-bold"
                  >
                    <FaWhatsapp className="mr-2" />
                    {t('roadmap.readyToBegin.whatsappButton')}
                  </button>
                </div>
                <a
                  href="/"
                  className="bg-transparent border-2 border-white text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-sm md:text-lg font-bold hover:bg-white/10 transition-all flex items-center justify-center"
                >
                  {t('roadmap.readyToBegin.learnMore')} <FaArrowRight className="ml-2" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default RoadMapPage;